import { observable, action, computed } from 'mobx';
import sumBy from 'lodash/sumBy';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    IInvoiceSummaryBlockResponse,
    IInvoiceSummaryVariableValue
} from '~/types/server.types';
import { DiscountTypes, IInvoiceSummary, TaxType } from '~/types/model.types';
import type { ServiceItemBlock } from '~/models/blocks/service-item-block.model';
import type { ServicesBlock } from '~/models/blocks/services-block.model';
import type { InvoiceBlock } from '~/models/blocks/invoice-block.model';
import { Variable } from '~/models/variable.model';
import { getDiscountedAmount, toFixedIfNotRound } from '~/utils/money.utils';
import type { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { taxAmountWithDiscounts } from '~/components/Payments/utils';

type InvoiceSummaryBlockDataForServer = ReturnType<
    BaseBlock['getDataForServer']
> &
    Pick<IInvoiceSummaryBlockResponse, 'type' | 'invoice_summary'>;

export class InvoiceSummaryBlock extends BaseBlock {
    type: 'invoice_summary_block';
    @observable invoice_summary: IInvoiceSummary;
    @observable isVariablesUpdated: boolean = true;
    @observable connected_variable_id: string;

    constructor(
        block: IInvoiceSummaryBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    @computed
    get totalLateFees() {
        const {
            builderUiStore: {
                selectedFlow: { paymentScheduleBlock }
            }
        } = this.rootStore;

        return sumBy(paymentScheduleBlock?.paymentBlocks, 'lateFee');
    }

    @computed
    get isLateFeeEnabled() {
        return !!this.invoice_summary.late_fee_enabled;
    }

    @computed
    get lateFeePercentage() {
        return this.invoice_summary.late_fee_percentage;
    }

    @computed
    get isClientPaymentPlan() {
        return (
            !this.invoiceBlock.isRecurringInvoice &&
            this.invoice_summary.is_client_payment_plan
        );
    }

    @computed
    get maxNumberOfPayments() {
        return this.invoice_summary.max_number_of_payments;
    }

    @computed
    get numberOfPayments() {
        return this.connectedVariable?.value.number_of_payments;
    }

    @computed
    get isClientSelectedPaymentPlan() {
        return this.isClientPaymentPlan && this.numberOfPayments;
    }

    @computed
    get isPendingClientPaymentSelection() {
        return this.isClientPaymentPlan && !this.numberOfPayments;
    }

    @computed
    get isClientSelectedPayInFullPlan() {
        return this.isClientPaymentPlan && this.numberOfPayments === 1;
    }

    @computed
    get isPayInFullDiscountApplied() {
        return (
            this.allowPayInFullDiscount && this.isClientSelectedPayInFullPlan
        );
    }

    @computed
    get firstPaymentDueDateType() {
        return this.invoice_summary.first_payment_due_date_type;
    }

    @computed
    get firstPaymentDueDate() {
        return this.invoice_summary.first_payment_due_date;
    }

    @computed
    get allowPayInFullDiscount() {
        return this.invoice_summary.allow_pay_in_full_discount;
    }

    @computed
    get payInFullDiscount() {
        return this.invoice_summary.pay_in_full_discount;
    }

    @computed
    get payInFullDiscountType() {
        return this.invoice_summary.pay_in_full_discount_type;
    }

    @computed
    get hasDiscount() {
        return !!this.invoice_summary.discount_type;
    }

    getTax(taxNumber: string) {
        return this.invoice_summary?.[taxNumber];
    }

    hasTax(taxNumber: string) {
        return !!this.invoice_summary?.[taxNumber]?.active;
    }

    @action.bound
    setIsVariablesUpdated(value: boolean) {
        this.isVariablesUpdated = value;
    }

    @computed
    get isDeletable() {
        return false;
    }

    @computed
    get isAllTaxesActive() {
        return (
            this.hasTax('tax_1') && this.hasTax('tax_2') && this.hasTax('tax_3')
        );
    }

    @computed
    get isAllTaxesDisabled() {
        return (
            !this.hasTax('tax_1') &&
            !this.hasTax('tax_2') &&
            !this.hasTax('tax_3')
        );
    }

    @computed
    get errors() {
        const {
            builderUiStore: { showErrors, selectedFlow }
        } = this.rootStore;

        if (selectedFlow.is_template || !showErrors) {
            return {};
        }

        const errors: any = {};

        // Discount validations
        if (this.invoice_summary?.discount_type) {
            if (
                this.hasErrorWithFixedDiscount(
                    this.invoice_summary?.discount_type,
                    this.invoice_summary?.discount,
                    this.minimalUnpaidSubTotal
                )
            ) {
                errors.hasErrors = true;
                errors.discountSubtotalValue = true;
            }

            if (
                this.hasErrorWithRelativeType(
                    this.invoice_summary?.discount_type,
                    this.invoice_summary?.discount
                )
            ) {
                errors.hasErrors = true;
                errors.discountRelativeValue = true;
            }
        }

        // Tax validations
        if (
            ['tax_1', 'tax_2', 'tax_3'].some(currTax => {
                return (
                    this.invoice_summary &&
                    this.invoice_summary[currTax] &&
                    this.invoice_summary[currTax].active &&
                    this.hasErrorWithRelativeType(
                        this.invoice_summary[currTax].type,
                        this.invoice_summary[currTax].value
                    )
                );
            })
        ) {
            errors.hasErrors = true;
            errors.taxRelativeValue = true;
        }

        return errors;
    }

    hasErrorWithRelativeType(selectedType: string, value: number) {
        return selectedType === TaxType.Relative && (value > 100 || value < 0);
    }

    hasErrorWithFixedDiscount(
        selectedType: string,
        value: number,
        maxValue: number
    ) {
        return selectedType === TaxType.Absolute && value > maxValue;
    }

    @computed
    get invoiceBlock() {
        return this.parent as InvoiceBlock;
    }

    taxAmount(
        taxNumber: string,
        {
            withPayInFullDiscount = this.isPayInFullDiscountApplied,
            shouldCalculateTaxInBE = true
        } = {}
    ) {
        if (this.isVariablesUpdated && shouldCalculateTaxInBE) {
            return this.connectedVariable?.value[taxNumber];
        }
        const isTaxOnSomeService = (
            this.parent as InvoiceBlock
        ).invoiceServicesBlock.isSomeServiceTaxed(taxNumber);

        if (
            !this.invoice_summary[taxNumber] ||
            (!this.invoice_summary[taxNumber].active && !isTaxOnSomeService)
        ) {
            return 0;
        }
        if (this.invoice_summary[taxNumber]?.type === TaxType.Absolute) {
            return this.invoice_summary[taxNumber]?.value;
        }
        const servicesWithTax = this.getServicesWithTax(taxNumber);

        return taxAmountWithDiscounts(
            this.invoice_summary,
            this.subTotal,
            servicesWithTax,
            taxNumber,
            withPayInFullDiscount
        );
    }

    @computed
    get subTotal() {
        if (this.isVariablesUpdated) {
            return this.connectedVariable?.value?.sub_total;
        }
        return sumBy(
            (this.parent as InvoiceBlock).allMainServiceItemBlocks,
            (serviceItem: ServiceItemBlock) => {
                let { totalPrice } = serviceItem;
                if (serviceItem.isPackage) {
                    totalPrice += sumBy(
                        serviceItem.childBlocks,
                        (packageServiceItem: ServiceItemBlock) => {
                            return packageServiceItem.totalPrice;
                        }
                    );
                }
                return totalPrice;
            }
        );
    }

    @computed
    get discountAmount() {
        // Using total_discount for backward compatibility:
        // Smart files created AFTER the rollout of 'client payment plan', will have the 'member_discount' field
        // In which, the 'total_discount' represents the combined value of two discounts: 'member_discount' and 'pay_in_full_discount'
        // Smart files created BEFORE the rollout of 'client payment plan', will NOT have the 'member_discount' field
        // In which, the 'total_discount' represents the 'member_discount'
        if (this.isVariablesUpdated) {
            const memberDiscount =
                this.connectedVariable?.value?.member_discount;

            return (
                memberDiscount ?? this.connectedVariable?.value?.total_discount
            );
        }

        if (this.isRelativeDiscount()) {
            return this.subTotal * (this.invoice_summary.discount / 100);
        }
        return this.invoice_summary.discount || 0;
    }

    private calcGrandTotal({
        withPayInFullDiscount = this.isPayInFullDiscountApplied,
        shouldCalculateTaxInBE = true
    } = {}) {
        const payInFullDiscount = withPayInFullDiscount
            ? this.calculatedPayInFullDiscount
            : 0;

        return (
            this.subTotal -
            this.discountAmount -
            payInFullDiscount +
            this.taxAmount('tax_1', {
                withPayInFullDiscount,
                shouldCalculateTaxInBE
            }) +
            this.taxAmount('tax_2', {
                withPayInFullDiscount,
                shouldCalculateTaxInBE
            }) +
            this.taxAmount('tax_3', {
                withPayInFullDiscount,
                shouldCalculateTaxInBE
            })
        );
    }

    @computed
    get grandTotal() {
        if (this.isVariablesUpdated) {
            return this.connectedVariable?.value?.total_price;
        }
        return this.calcGrandTotal();
    }

    @computed
    get grandTotalWithFees() {
        return this.grandTotal + this.totalLateFees;
    }

    @computed
    get payInFullDiscountAmount() {
        if (this.isVariablesUpdated) {
            return this.connectedVariable?.value?.pay_in_full_discount;
        }

        return this.calculatedPayInFullDiscount;
    }

    @computed
    get calculatedPayInFullDiscount() {
        if (typeof this.payInFullDiscount !== 'number') {
            return 0;
        }
        if (this.payInFullDiscountType === DiscountTypes.Absolute) {
            return this.payInFullDiscount;
        }

        return (
            (this.subTotal - this.discountAmount) *
            (this.payInFullDiscount / 100)
        );
    }

    @computed
    get grandTotalWithFeesAfterPayInFullDiscount() {
        return (
            this.calcGrandTotal({
                withPayInFullDiscount: true,
                shouldCalculateTaxInBE: false
            }) + this.totalLateFees
        );
    }

    @computed
    get minimalUnpaidGrandTotal() {
        const minimalTotal = this.minimalUnpaidSubTotal;

        const discountedPrice = this.hasDiscount
            ? getDiscountedAmount(
                  minimalTotal,
                  this.invoice_summary.discount,
                  this.invoice_summary.discount_type
              )
            : minimalTotal;

        const tax = this.invoiceBlock.hasSelection
            ? 0
            : this.taxAmount('tax_1') +
              this.taxAmount('tax_2') +
              this.taxAmount('tax_3');

        return discountedPrice + tax;
    }

    @computed
    get minimalUnpaidSubTotal() {
        if (this.invoiceBlock.hasPaidPayments) {
            return this.invoiceBlock.invoiceSummaryBlock.subTotal;
        } else {
            const fixedItems = this.invoiceBlock.invoiceFixedItems;

            const fixedTotal: number = sumBy(
                fixedItems,
                (serviceItem: ServiceItemBlock) =>
                    serviceItem.totalPriceIncludingSubItems
            );

            return toFixedIfNotRound(
                fixedTotal + this.invoiceBlock.cheapestSelectablePrice
            );
        }
    }

    @computed
    get minimalUnpaidSubTotalAfterDiscount() {
        return this.minimalUnpaidSubTotal - this.discountAmount;
    }

    get connectedVariable(): Variable<IInvoiceSummaryVariableValue> {
        return super.connectedVariable;
    }

    isDraggable() {
        return false;
    }

    dndAcceptedTypes() {
        return null;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    getDataForServer(): InvoiceSummaryBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'invoice_summary_block',
            invoice_summary: { ...this.invoice_summary }
        };
    }

    isRelativeDiscount() {
        return this.invoice_summary.discount_type === DiscountTypes.Relative;
    }

    getServicesWithTax(taxNumber: string) {
        const mainPackageItems = [
            ...((
                (this.parent as InvoiceBlock)
                    .invoiceServicesBlock as ServicesBlock
            ).allDescendants() as ServiceItemBlock[]),
            ...(this.parent as InvoiceBlock)
                .clientSelectedServiceItemsBlocksWithSubItems
        ];
        return mainPackageItems.filter((serviceItemBlock: ServiceItemBlock) => {
            return serviceItemBlock.service_item[taxNumber];
        }, []);
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);

        if (
            'connected_variable_id' in attributes &&
            block.connected_variable_id
        ) {
            this.connected_variable_id = block.connected_variable_id;
        }
    }
}
